import { logger } from '@adornis/base/logging.js';
import { sendOTP } from '@adornis/buchungsheld-db/api/RegistrationConfirmationCode.js';
import { isHexColor } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import { RegistrationConfirmationCode } from '@adornis/buchungsheld-db/entities/RegistrationConfirmationCode.js';
import { RegistrationTooManyAttemptsError } from '@adornis/buchungsheld-db/errors/RegistrationTooManyAttemptsError.js';
import { RegistrationWrongCodeError } from '@adornis/buchungsheld-db/errors/RegistrationWrongCodeError.js';
import { UserNotFoundError } from '@adornis/buchungsheld-db/errors/UserNotFoundError.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { subMinutes } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BuchungsheldLitElement } from '../BuchungsheldLitElement.js';
import { buttonColorStyle, resolvedButtonFontColor, type ButtonColors } from '../buttonColorStyles.js';

/**
 * @element bh-component-action-otp-login
 */
@customElement('bh-component-action-otp-login')
export class BhComponentActionOtpLogin extends BuchungsheldLitElement {
  @property({ attribute: 'user-id' }) userID?: string;
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: 'email' }) email?: string;

  // login button colors from the global color settings, passed down by bh-login
  // so the OTP step matches the rest of the login flow
  @property({ attribute: false }) buttonColors?: ButtonColors;

  @state() private _status?: string;
  @state() private _authCode?: string;

  private static lastAutomaticOTPSend = new Map<string, Date>();

  override connectedCallback(): void {
    super.connectedCallback();

    // need the lastAutomaticOTPSend timestamp to avoid sending too many login tokens
    // caused by a short loading state when user is logging in before error of registrationController gets cleared
    // causing this component to be reattached, thus calling connectedCallback again
    const constr = this.constructor as typeof BhComponentActionOtpLogin;
    if (
      !constr.lastAutomaticOTPSend.get(`(${this.userID},${this.email})`) ||
      constr.lastAutomaticOTPSend.get(`(${this.userID},${this.email})`)! < subMinutes(new Date(), 5)
    ) {
      void this._sendCode();
      constr.lastAutomaticOTPSend.set(`(${this.userID},${this.email})`, new Date());
    }
  }

  override render() {
    return html`
      <x-flex center crossaxis-center h100 w100>
        <x-flex
          ${css({ background: '#fff', borderRadius: this.sizes.borderRadius })}
          space="md"
          crossaxis-center
          padding="xl"
        >
          <x-icon
            ${css({
              padding: this.spacing.md,
              borderRadius: '50%',
              background: isHexColor(this.buttonColors?.buttonColor)
                ? this.buttonColors.buttonColor
                : this.colors.accent,
              color: resolvedButtonFontColor(this.buttonColors) ?? this.colors.white,
              fontSize: '1.5em',
            })}
          >
            shield
          </x-icon>
          <tr-h3 bold> ${this._translation.translate('OTP', 'title')} </tr-h3>
          <tr-text ${css({ width: 'min(300px, 100%)', textAlign: 'center' })}>
            ${this._translation.translate('OTP', 'subtitle')}.
          </tr-text>
          ${
            this._status
              ? html`
                  <tr-text ${css({ width: 'min(300px, 100%)', textAlign: 'center', color: this.colors.tone.error })}>
                    ${this._status}
                  </tr-text>
                `
              : nothing
          }
          <input
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]{6}"
            aria-label="Sechsstelliger Login-Code"
            ${css({
              width: '10ch',
              height: '40px',
              textAlign: 'center',
              fontSize: '20px',
              letterSpacing: '0.2em',
              border: `2px solid ${this.colors.neutral.shade(100)}`,
              borderRadius: this.sizes.borderRadius,
            })}
            @input=${(event: Event) => {
              const input = event.target as HTMLInputElement;
              input.value = input.value.replace(/\D/g, '').slice(0, 6);
              this._authCode = input.value;
            }}
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === 'Enter') void this._submitCode();
            }}
          />
          <x-button ${buttonColorStyle(this.buttonColors)} @click=${() => this._submitCode()}>
            <tr-h4 bold> ${this._translation.translate('OTP', 'check')} </tr-h4>
          </x-button>
          <x-button mode="text" icon="refresh" @click=${() => this._sendCode(true)}>
            ${this._translation.translate('OTP', 'resendCode')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _sendCode(resend = false) {
    if (this.userID || this.email) {
      try {
        await XDialog.waitFor(sendOTP(this.userID, this.email, this.institutionID)());
        if (resend) {
          void XSnackbar.show(this._translation.translate('REGISTRATION', 'successfullyResendConfirmationCode'));
        }
      } catch (err) {
        let errMsg = '';
        let errDsc = '';
        switch ((err as any)?._class) {
          case UserNotFoundError._class: {
            errMsg = 'Benutzer existiert nicht';
            errDsc = this._translation.translate('OTP', 'mail_login_user_non_existent_message') as string;
            break;
          }

          default: {
            errMsg = 'Es gab ein Problem beim Versenden des OTP';
            errDsc = resend
              ? (this._translation.translate('REGISTRATION', 'problemOccuredResendingOTP') as string)
              : (this._translation.translate('OTP', 'problemOccuredSendingOTP') as string);
          }
        }

        logger.error({ err }, errMsg);
        void XSnackbar.show(errDsc);
      }
    }
  }

  private async _submitCode() {
    const authCode = this._authCode;
    if (!authCode || !/^[0-9]{6}$/.test(authCode)) {
      return XSnackbar.show(this._translation.translate('REGISTRATION', 'noOTPEntered'));
    }
    try {
      await XDialog.waitFor(RegistrationConfirmationCode.loginWithOTP(authCode, this.email, this.userID));
      this.dispatchEvent(new CustomEvent('login'));
    } catch (error: any) {
      logger.error({ err: error }, 'Es gab ein Problem beim Login mit OTP');
      this._handleError(error);
    }
  }

  private _handleError(error: any) {
    switch (error?._class) {
      case RegistrationWrongCodeError._class:
      case RegistrationTooManyAttemptsError._class:
        this._status = this._translation.translate('errors', error._class, 'explanation') as unknown as string;
        break;
      default:
    }
  }
}
